import React, { useState, useEffect } from 'react'
import { useRequests } from '../../../../requests/useRequests'
import { useDispatch, useSelector } from 'react-redux'
import { RefreshActions } from '../../../../state/refresh'
import Modal from '../../../Modal/Modal'
import { RootState } from '../../../../state/store'
import { useNavigate } from 'react-router-dom'

interface ItemComment {
  id: string
  content: string
  createdAt: string
  updatedAt: string
  authorId: string
  authorName: string
}

interface ItemCommentsModalProps {
  show: boolean
  onClose: () => void
  projectId: string
  itemId: string
  itemName: string
}

const ItemCommentsModal: React.FC<ItemCommentsModalProps> = ({
  show,
  onClose,
  projectId,
  itemId,
  itemName
}) => {
  const { id, username } = useSelector((state: RootState) => state.user)
  const [comments, setComments] = useState<ItemComment[]>([])
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [editingComment, setEditingComment] = useState<string | null>(null)
  const [editedComment, setEditedComment] = useState('')
  const requestMaker = useRequests()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    if (show) {
      fetchComments()
    }
  }, [show, itemId])

  const fetchComments = async () => {
    setLoading(true)
    try {
      const response = await requestMaker.getProjectItemComments(projectId, itemId)
      setComments(response.data)
    } catch (error) {
      console.error('Błąd podczas pobierania komentarzy', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się pobrać komentarzy'
        })
      )
    } finally {
      setLoading(false)
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim()) return

    try {
      const response = await requestMaker.addProjectItemComment(
        projectId,
        itemId,
        { content: newComment }
      )

      setComments(prev => [...prev, response.data])
      setNewComment('')

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Komentarz został dodany'
        })
      )
    } catch (error) {
      console.error('Błąd podczas dodawania komentarza', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się dodać komentarza'
        })
      )
    }
  }

  const handleUpdateComment = async (commentId: string) => {
    if (!editedComment.trim()) return

    try {
      const response = await requestMaker.updateProjectItemComment(
        projectId,
        itemId,
        commentId,
        { content: editedComment }
      )

      setComments(prev => prev.map(comment =>
        comment.id === commentId ? response.data : comment
      ))

      setEditingComment(null)
      setEditedComment('')

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Komentarz został zaktualizowany'
        })
      )
    } catch (error) {
      console.error('Błąd podczas aktualizacji komentarza', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zaktualizować komentarza'
        })
      )
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten komentarz?')) return

    try {
      await requestMaker.deleteProjectItemComment(projectId, itemId, commentId)
      setComments(prev => prev.filter(comment => comment.id !== commentId))

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Komentarz został usunięty'
        })
      )
    } catch (error) {
      console.error('Błąd podczas usuwania komentarza', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć komentarza'
        })
      )
    }
  }

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={`Komentarze - ${itemName}`}
    >
      <div className='comments-container'>
        {loading
          ? (
            <div className='text-center py-3'>
              <div className='spinner-border text-primary' role='status'>
                <span className='sr-only'>Ładowanie komentarzy...</span>
              </div>
            </div>
            )
          : (
            <>
              {comments.length > 0
                ? (
                  <div className='comments-list mb-3' style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {comments.map(comment => (
                      <div key={comment.id} className='comment-item p-3 mb-2' style={{ backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                        <div className='d-flex justify-content-between'>
                          <div>
                  <h6 className='mb-1'>{comment.authorName ?? 'Klient'}</h6>
                  <p className='text-muted small'>
                            <i className='fa fa-clock mr-1' />
                            {new Date(comment.createdAt).toLocaleString()}
                            {comment.createdAt !== comment.updatedAt && ' (edytowany)'}
                          </p>
                </div>
                          {id === Number(comment.authorId) && (
                  <div>
                            {editingComment === comment.id
                              ? (
                  <>
                                <button
                                  className='btn btn-sm btn-success mr-1'
                                  onClick={async () => await handleUpdateComment(comment.id)}
                                >
                                  <i className='fa fa-check' />
                                </button>
                                <button
                                  className='btn btn-sm btn-secondary'
                                  onClick={() => {
                                    setEditingComment(null)
                                    setEditedComment('')
                                  }}
                                >
                                  <i className='fa fa-times' />
                                </button>
                              </>
                                )
                              : (
                  <>
                                <button
                                  className='btn btn-sm btn-link text-primary mr-1'
                                  onClick={() => {
                                    setEditingComment(comment.id)
                                    setEditedComment(comment.content)
                                  }}
                                >
                                  <i className='fa fa-edit' />
                                </button>
                                <button
                                  className='btn btn-sm btn-link text-danger'
                                  onClick={async () => await handleDeleteComment(comment.id)}
                                >
                                  <i className='fa fa-trash' />
                                </button>
                              </>
                                )}
                          </div>
                )}
                        </div>
                        {editingComment === comment.id
                          ? (
                  <div className='mt-2'>
                            <textarea
                              className='form-control'
                              value={editedComment}
                              onChange={(e) => setEditedComment(e.target.value)}
                              rows={2}
                            />
                          </div>
                            )
                          : (
                  <div className='mt-2'>
                            <p className='mb-0'>{comment.content}</p>
                          </div>
                            )}
                      </div>
                    ))}
                  </div>
                  )
                : (
                  <div className='alert alert-info'>
                    Brak komentarzy. {username ? 'Dodaj pierwszy komentarz do tego elementu.' : 'Zaloguj się, aby dodać komentarz.'}
                  </div>
                  )}

              {username
                ? (
                  <div className='add-comment-form'>
                    <div className='input-group'>
                      <textarea
                        className='form-control'
                        rows={2}
                        placeholder='Twój komentarz...'
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                      />
                    </div>
                    <div className='mt-2 text-right'>
                      <button
                        className={`
                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                      tw-text-white tw-min-w-[120px]
                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                      tw-flex tw-items-center tw-justify-center tw-gap-2
                      ${!newComment.trim() ? 'tw-opacity-75' : ''}
                    `}
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                      >
                        Dodaj komentarz
                      </button>
                    </div>
                  </div>
                  )
                : (
                  <div className='alert alert-info mt-3'>
                    Aby dodawać komentarze, <button onClick={() => navigate('/login')} className='btn btn-link p-0'>zaloguj się</button>
                  </div>
                  )}
            </>
            )}
      </div>
    </Modal>
  )
}

export default ItemCommentsModal
